import {
  computeInvoiceTaxCents,
  computeInvoiceTotalCents,
  computeLineAmountCents,
  type LineAmountInput,
} from '../../shared/format.ts';
import {
  INVALID_INVOICE_INPUT_ERROR,
  UpsertInvoiceInputSchema,
  type Currency,
  type InvoiceDetail,
  type InvoiceSnapshot,
} from '../../shared/invoice.ts';

export interface PdfInvoiceModel {
  id?: string;
  number: number;
  date: string;
  dueDate: string | null;
  currency: Currency;
  sender: {
    id: string;
    name: string;
    signaturePreviewUrl: string | null;
    bankRequisites: string;
  };
  billTo: PdfParty | null;
  shipTo: PdfParty | null;
  items: PdfInvoiceItem[];
  notes: string;
  taxRate: string;
  taxLabel: string | null;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
}

export interface PdfParty {
  name: string;
  email: string | null;
  billTo: string | null;
  shipTo: string | null;
}

export interface PdfInvoiceItem {
  description: string;
  quantity: string;
  rate: string;
  amountCents: number;
}

export interface PdfInvoiceDraftInput {
  number: number;
  date: string;
  dueDate: string | null;
  currency: Currency;
  sender: PdfInvoiceModel['sender'];
  billTo: PdfParty | null;
  shipTo: PdfParty | null;
  notes: string;
  items: Array<{ description: string; quantity: string; rate: string }>;
  taxRate: string;
  taxLabel: string | null;
}

export type PdfInvoiceDraftResult =
  | { ok: true; model: PdfInvoiceModel }
  | { ok: false; reason: 'invalid-form'; error: string };

export const PDF_INVALID_FORM_ERROR = INVALID_INVOICE_INPUT_ERROR;

export function invoiceDetailToPdfModel(invoice: InvoiceDetail): PdfInvoiceModel {
  const items = invoice.items.map((item) => ({
    description: item.description.trim(),
    quantity: item.quantity,
    rate: item.rate,
    amountCents: item.amountCents,
  }));

  return {
    id: invoice.id,
    number: invoice.number,
    date: invoice.date,
    dueDate: invoice.dueDate,
    currency: invoice.currency,
    sender: {
      id: invoice.senderProfile.id.trim(),
      name: invoice.senderProfile.name.trim(),
      signaturePreviewUrl: normalizeSignaturePreviewUrl(invoice.senderProfile),
      bankRequisites: invoice.senderProfile.bankRequisites,
    },
    billTo: normalizeInvoiceSnapshot(invoice.billToSnapshot),
    shipTo: normalizeInvoiceSnapshot(invoice.shipToSnapshot),
    items,
    notes: normalizeNotes(invoice.notes),
    taxRate: invoice.taxRate,
    taxLabel: invoice.taxLabel,
    subtotalCents: invoice.subtotalCents,
    taxCents: invoice.taxCents,
    totalCents: invoice.totalCents,
  };
}

export function invoiceDraftToPdfModel(input: PdfInvoiceDraftInput): PdfInvoiceDraftResult {
  if (!Number.isInteger(input.number) || input.number < 1) return invalidDraftResult();

  const sender = normalizeSender(input.sender);
  if (!sender) return invalidDraftResult();

  try {
    const candidateItems = input.items
      .filter((item) => !isBlankDraftItem(item))
      .map((item) => ({
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
      }));
    const parsed = UpsertInvoiceInputSchema.parse({
      date: input.date,
      dueDate: input.dueDate,
      currency: input.currency,
      notes: input.notes,
      items: candidateItems,
      taxRate: input.taxRate,
      taxLabel: input.taxLabel,
    });
    const items = parsed.items.map((item) => ({
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
      amountCents: computeLineAmountCents(item),
    }));
    const lineInputs = items.map(({ quantity, rate }) => ({ quantity, rate }));
    const subtotalCents = computeInvoiceTotalCents(lineInputs);
    const taxCents = computeInvoiceTaxCents(subtotalCents, parsed.taxRate);

    return {
      ok: true,
      model: {
        number: input.number,
        date: parsed.date,
        dueDate: parsed.dueDate,
        currency: parsed.currency,
        sender,
        billTo: normalizePdfParty(input.billTo),
        shipTo: normalizePdfParty(input.shipTo),
        items,
        notes: normalizeNotes(parsed.notes),
        taxRate: parsed.taxRate,
        taxLabel: parsed.taxLabel,
        subtotalCents,
        taxCents,
        totalCents: subtotalCents + taxCents,
      },
    };
  } catch {
    return invalidDraftResult();
  }
}

function normalizeSender(sender: PdfInvoiceModel['sender']): PdfInvoiceModel['sender'] | null {
  const id = sender.id.trim();
  const name = sender.name.trim();
  if (!id || !name) return null;
  return {
    id,
    name,
    signaturePreviewUrl: normalizeOptionalString(sender.signaturePreviewUrl),
    bankRequisites: sender.bankRequisites,
  };
}

function normalizeInvoiceSnapshot(snapshot: InvoiceSnapshot | null): PdfParty | null {
  if (!snapshot) return null;
  return normalizePdfParty(snapshot);
}

function normalizePdfParty(party: PdfParty | InvoiceSnapshot | null): PdfParty | null {
  if (!party) return null;
  const name = party.name.trim();
  if (!name) return null;
  return {
    name,
    email: normalizeOptionalString(party.email),
    billTo: normalizeOptionalString(party.billTo),
    shipTo: normalizeOptionalString(party.shipTo),
  };
}

function normalizeSignaturePreviewUrl(
  senderProfile: InvoiceDetail['senderProfile'],
): string | null {
  const maybeSenderProfile = senderProfile as InvoiceDetail['senderProfile'] & {
    signaturePreviewUrl?: unknown;
  };
  return typeof maybeSenderProfile.signaturePreviewUrl === 'string'
    ? normalizeOptionalString(maybeSenderProfile.signaturePreviewUrl)
    : null;
}

function normalizeOptionalString(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function normalizeNotes(notes: string): string {
  return notes.trim() === '' ? '' : notes;
}

function isBlankDraftItem(item: LineAmountInput & { description: string }): boolean {
  return item.description.trim() === '' && item.quantity.trim() === '' && item.rate.trim() === '';
}

function invalidDraftResult(): PdfInvoiceDraftResult {
  return { ok: false, reason: 'invalid-form', error: PDF_INVALID_FORM_ERROR };
}
